import { useMemo, useState } from 'react';
import { Box, Button, Divider, Group, Paper, Stack, Stepper, Text, Title } from '@mantine/core';
import { useBreakpointUp } from '@/shared/responsive/useBreakpoint';
import { ChevronLeft, X } from 'lucide-react';
import type { ProviderAuthMethod, ProviderType } from '@bmai/shared';
import type { IntegrationsPageViewModel, IntegrationSaveResult } from '../IntegrationsPageViewModel';
import ProviderIconComponent from '@/shared/ProviderIconComponent';
import ConnectionStepPrerequisites from './ConnectionStepPrerequisites';
import ConnectionStepChooseAuth from './ConnectionStepChooseAuth';
import ConnectionStepFields from './ConnectionStepFields';
import ConnectionStepAuth, { type LaunchUrlResolver } from './ConnectionStepAuth';
import ConnectionStepVerify from './ConnectionStepVerify';
import { inferConnectionSteps, type WizardStep } from './inferConnectionSteps';

interface Props {
    vm: IntegrationsPageViewModel;
    providerType: ProviderType;
    onClose: () => void;
    onSaveResult: (result: IntegrationSaveResult) => void;
    /** Returns the OAuth result so the wizard can advance on success. */
    onStartOauth: () => Promise<IntegrationSaveResult>;
    /** Returns the install result so the wizard can advance to verify on success. */
    onStartInstall: () => Promise<IntegrationSaveResult>;
    onSubmitInstallFallback: (value: string) => Promise<IntegrationSaveResult>;
    onReauthenticate?: () => void;
    /** Optional client-side resolver for launch-action URLs. Falls back to server callback when omitted or returns null. */
    onResolveLaunchUrl?: LaunchUrlResolver;
}

/**
 * Generic schema-driven connection wizard. Builds its step list from the
 * provider's metadata (setupInstructions, authTypes, preOAuth fields,
 * connectionFields) via `inferConnectionSteps`. No per-provider conditionals
 * here — adding a new provider Just Works as long as it populates the
 * standard ProviderType shape.
 */
export default function IntegrationConnectionWizard({
    vm,
    providerType,
    onClose,
    onSaveResult,
    onStartOauth,
    onStartInstall,
    onSubmitInstallFallback,
    onReauthenticate,
    onResolveLaunchUrl,
}: Props): React.ReactElement {
    const [acknowledged, setAcknowledged] = useState(vm.editing);
    const [installFallbackMode, setInstallFallbackMode] = useState(false);
    const [installFallbackValue, setInstallFallbackValue] = useState('');

    const steps: WizardStep[] = useMemo(
        () =>
            inferConnectionSteps({
                providerType,
                mode: vm.editing ? 'edit' : 'create',
                selectedAuthMethod: vm.form.authMethod,
            }),
        [providerType, vm.editing, vm.form.authMethod],
    );

    const [active, setActive] = useState(() => (vm.editing ? Math.max(0, steps.length - 1) : 0));

    const current = steps[active];

    function canAdvance(): boolean {
        if (!current) return false;
        switch (current.kind) {
            case 'prerequisites':
                return !current.requireAcknowledgement || acknowledged;
            case 'chooseAuth':
                return vm.form.authMethod !== null;
            case 'preAuthConfig':
            case 'config':
                return current.fields.every((f) => !f.required || hasValue(vm.form.config[f.name]));
            case 'auth':
                if (current.method === 'OAuth' || current.installFlow) {
                    return true;
                }
                return current.fields.every((f) => !f.required || hasValue(vm.form.config[f.name]));
            case 'verify':
                return true;
        }
    }

    async function handleSave(): Promise<void> {
        const result = await vm.save();
        onSaveResult(result);
    }

    function advanceToVerify(): void {
        const idx = steps.findIndex((s) => s.kind === 'verify');
        if (idx >= 0) setActive(idx);
    }

    function advanceOneStep(): void {
        setActive((a) => Math.min(steps.length - 1, a + 1));
    }

    async function handleStartOauth(): Promise<void> {
        const result = await onStartOauth();
        if (result.ok) advanceOneStep();
    }

    async function handleStartInstall(): Promise<void> {
        const result = await onStartInstall();
        if (result.ok) advanceToVerify();
    }

    async function handleSubmitInstallFallback(): Promise<void> {
        const result = await onSubmitInstallFallback(installFallbackValue);
        if (result.ok) advanceToVerify();
    }

    const currentStep = steps[active];
    const isWide = useBreakpointUp('sm');

    return (
        <Box maw={760} mx="auto">
            <Paper withBorder radius="md" p="xl">
                <Group justify="space-between" align="center" wrap="nowrap" mb="lg">
                    <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                        <ProviderIconComponent providerTypeId={providerType.id} size={24} />
                        <Stack gap={0} style={{ minWidth: 0 }}>
                            <Text size="xs" c="dimmed" tt="uppercase" fw={600} lh={1}>
                                {vm.editing ? 'Edit connection' : 'New connection'}
                            </Text>
                            <Title order={4} lh={1.2}>{providerType.displayName}</Title>
                        </Stack>
                    </Group>
                    <Button variant="subtle" size="sm" color="gray" leftSection={<X size={14} />} onClick={onClose} style={{ flexShrink: 0 }}>
                        Cancel
                    </Button>
                </Group>

                <Stepper
                    active={active}
                    onStepClick={(s) => setActive(s)}
                    allowNextStepsSelect={vm.editing}
                    size="sm"
                    iconSize={28}
                    orientation={isWide ? 'horizontal' : 'vertical'}
                >
                    {steps.map((step, i) => (
                        <Stepper.Step key={`${step.kind}-${i}`} label={step.label} description={step.description} />
                    ))}
                </Stepper>

                <Divider my="lg" />

                <Stack gap="xs" mb="lg">
                    <Title order={5}>{currentStep?.label}</Title>
                    {currentStep?.description && (
                        <Text size="sm" c="dimmed">{currentStep.description}</Text>
                    )}
                </Stack>
                <StepContent
                    step={currentStep}
                    vm={vm}
                    providerType={providerType}
                    acknowledged={acknowledged}
                    onAcknowledgedChange={setAcknowledged}
                    installFallbackMode={installFallbackMode}
                    installFallbackValue={installFallbackValue}
                    onInstallFallbackValueChange={setInstallFallbackValue}
                    onSwitchToInstallFallback={() => setInstallFallbackMode(true)}
                    onBackFromInstallFallback={() => setInstallFallbackMode(false)}
                    onStartOauth={handleStartOauth}
                    onStartInstall={handleStartInstall}
                    onSubmitInstallFallback={handleSubmitInstallFallback}
                    onReauthenticate={onReauthenticate}
                    onResolveLaunchUrl={onResolveLaunchUrl}
                />
            </Paper>

            <Group justify="space-between" mt="lg">
                <Button
                    variant="default"
                    onClick={() => setActive((a) => Math.max(0, a - 1))}
                    disabled={active === 0}
                    leftSection={<ChevronLeft size={14} />}
                >
                    Back
                </Button>
                {currentStep?.kind === 'verify' ? (
                    <Button onClick={handleSave} loading={vm.saving}>
                        {vm.editing ? 'Update connection' : 'Save connection'}
                    </Button>
                ) : (
                    <Button
                        onClick={() => setActive((a) => Math.min(steps.length - 1, a + 1))}
                        disabled={!canAdvance()}
                    >
                        Next
                    </Button>
                )}
            </Group>
        </Box>
    );
}

interface StepContentProps {
    step: WizardStep | undefined;
    vm: IntegrationsPageViewModel;
    providerType: ProviderType;
    acknowledged: boolean;
    onAcknowledgedChange: (value: boolean) => void;
    installFallbackMode: boolean;
    installFallbackValue: string;
    onInstallFallbackValueChange: (value: string) => void;
    onSwitchToInstallFallback: () => void;
    onBackFromInstallFallback: () => void;
    onStartOauth: () => void;
    onStartInstall: () => void;
    onSubmitInstallFallback: () => void;
    onReauthenticate?: () => void;
    onResolveLaunchUrl?: LaunchUrlResolver;
}

function StepContent(props: StepContentProps): React.ReactElement {
    const { step, vm, providerType } = props;
    if (!step) return <></>;
    switch (step.kind) {
        case 'prerequisites':
            if (!providerType.setupInstructions) return <Text size="sm">No prerequisites.</Text>;
            return (
                <ConnectionStepPrerequisites
                    instructions={providerType.setupInstructions}
                    acknowledged={props.acknowledged}
                    showAcknowledgement={step.requireAcknowledgement}
                    onAcknowledgedChange={props.onAcknowledgedChange}
                />
            );
        case 'chooseAuth':
            return (
                <ConnectionStepChooseAuth
                    options={step.options}
                    value={vm.form.authMethod}
                    onChange={(m: ProviderAuthMethod) => vm.setAuthMethod(m)}
                />
            );
        case 'preAuthConfig':
        case 'config':
            return (
                <ConnectionStepFields
                    fields={step.fields}
                    config={vm.form.config}
                    editing={vm.editing}
                    onFieldChange={(name, value) => vm.setFieldValue(name, value)}
                    onLoadOptions={(route) => vm.fetchFieldOptions(route)}
                />
            );
        case 'auth':
            return (
                <ConnectionStepAuth
                    providerType={providerType}
                    method={step.method}
                    fields={step.fields}
                    launchActions={step.launchActions}
                    config={vm.form.config}
                    editing={vm.editing}
                    saving={vm.saving}
                    installFlow={step.installFlow ? providerType.installFlow : undefined}
                    installFallbackMode={props.installFallbackMode}
                    installFallbackValue={props.installFallbackValue}
                    onFieldChange={(name, value) => vm.setFieldValue(name, value)}
                    onLoadOptions={(route) => vm.fetchFieldOptions(route)}
                    onFetchTenantSecret={(providerTypeId, route) =>
                        vm.fetchTenantSecret(providerTypeId, route)
                    }
                    onCallProviderRoute={(providerTypeId, route, body) =>
                        vm.callProviderRoute(providerTypeId, route, body)
                    }
                    onResolveLaunchUrl={props.onResolveLaunchUrl}
                    onStartOauth={props.onStartOauth}
                    onStartInstall={props.onStartInstall}
                    onSubmitInstallFallback={props.onSubmitInstallFallback}
                    onInstallFallbackValueChange={props.onInstallFallbackValueChange}
                    onSwitchToInstallFallback={props.onSwitchToInstallFallback}
                    onBackFromInstallFallback={props.onBackFromInstallFallback}
                    onReauthenticate={props.onReauthenticate}
                />
            );
        case 'verify':
            return (
                <ConnectionStepVerify
                    providerType={providerType}
                    config={vm.form.config}
                    displayName={vm.form.displayName}
                    enabled={vm.form.enabled}
                    canValidate={vm.editing || (vm.form.authMethod !== null && vm.form.authMethod !== 'OAuth')}
                    validation={vm.validation}
                    onDisplayNameChange={(v) => vm.setDisplayName(v)}
                    onEnabledChange={(v) => vm.setEnabled(v)}
                    onValidate={() => vm.validate()}
                />
            );
    }
}

function hasValue(value: unknown): boolean {
    if (value === undefined || value === null) return false;
    if (typeof value === 'string') return value.length > 0;
    if (Array.isArray(value)) return value.length > 0;
    return true;
}
